import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAllChatsQuery, useMyBotsQuery } from '../../hooks/queries';
import { sidebarChats } from '../../lib/sidebarChats';
import { chatIdFromUrl, chatPath } from '../bots/chatLinks';
import BotAvatar from '../bots/BotAvatar';
import ChatUnreadDot from '../unread/ChatUnreadDot';
import { IconSlot, SectionHeader, rowClass } from './SidebarParts';

export const RECENT_CHATS = 5;

/** The latest chats over every bot (BusyBee's too), each with its bot: of the newest few, what
 * waits for the person first, then the unread ones, then the newest (lib/sidebarChats.js). One
 * page of the Chats page's history (GET /bot-chats). */
export const useRecentChats = (limit = RECENT_CHATS) => {
  const { data: mine } = useMyBotsQuery();
  const { data } = useAllChatsQuery({ limit });
  return useMemo(() => {
    const bots = new Map([...(mine?.queen ? [mine.queen] : []), ...(mine?.bots || [])].map((b) => [b.id, b]));
    const chats = (data?.pages?.[0]?.chats || []).map((c) => ({ ...c, bot: bots.get(c.bot_id) || { id: c.bot_id } }));
    return sidebarChats(chats, limit);
  }, [mine, data, limit]);
};

/**
 * "Recent chats" in the sidebar: the five latest conversations with any bot in any project,
 * each with its bee and a dot when it waits for the person or has something unread, and
 * "All chats" (the one history, /app/chats).
 */
const SidebarRecentChats = ({ onItemClick }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const chats = useRecentChats();
  const active = chatIdFromUrl(`${location.pathname}${location.search}`);
  if (!chats.length) return null;
  return (
    <div className="flex flex-col gap-px" data-testid="sidebar-recent-chats">
      <SectionHeader>{t('bots.chat.recent_chats')}</SectionHeader>
      {chats.map((c) => (
        <Link key={c.id} to={chatPath(c.id)} state={{ chatId: c.id, botId: c.bot_id, projectId: c.project_id || null }}
          onClick={onItemClick} data-testid="sidebar-recent-chat"
          title={[c.title || t('bots.chat.new_chat'), c.project?.name].filter(Boolean).join(' · ')}
          className={rowClass(c.id === active)}>
          <IconSlot><BotAvatar bot={c.bot} size="xs" state={c.working ? 'working' : null} /></IconSlot>
          <span className={`flex-1 truncate ${c.unread ? 'font-medium text-foreground' : ''}`}>{c.title || t('bots.chat.new_chat')}</span>
          {c.pending_approvals > 0 ? <span className="w-1.5 h-1.5 rounded-full bg-attention flex-shrink-0" aria-label={t('bots.state.needs_you')} />
            : c.unread && <ChatUnreadDot />}
        </Link>
      ))}
      <Link to="/app/chats" onClick={onItemClick} data-testid="sidebar-all-chats"
        className={rowClass(location.pathname === '/app/chats', location.pathname === '/app/chats' ? '' : '!text-muted-foreground hover:!text-foreground')}>
        <IconSlot />
        <span className="flex-1 truncate text-[12.5px]">{t('bots.chat.all_chats')}</span>
      </Link>
    </div>
  );
};

export default SidebarRecentChats;
